import { useEffect, useRef, useState } from 'react';
import { Checkbox, Money, Switch, WideTable, type WideColumn } from '@/components/ui';
import { useTabId, usePane } from '@/components/layout/workspace/workspaceContext';
import { useScrollMemory } from '@/components/layout/workspace/useScrollMemory';
const ROWS = Array.from({ length: 24 }, (_, i) => ({
  id: String(i + 1),
  title: ['Обслуживание', 'Лицензии', 'Оборудование', 'Поддержка'][i % 4] + ' · ' + (i + 1),
}));
const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];
const amount = (row: number, month: number) => (row + 1) * 100000 + month * 12500;
export function WideTableDemo({ memoryScope = 'kaname.operations.table.v1' }: { memoryScope?: string }) {
  const tab = useTabId(),
    pane = usePane();
  const memoryKey = memoryScope + ':' + pane + ':' + (tab ?? 'gallery');
  const [count, setCount] = useState(() => {
    try {
      const value = Number(localStorage.getItem(memoryKey + ':count'));
      return Number.isInteger(value) ? Math.min(12, Math.max(8, value)) : 8;
    } catch {
      return 8;
    }
  });
  const [loading, setLoading] = useState(false),
    [error, setError] = useState<string>(),
    [fail, setFail] = useState(false),
    [automatic, setAutomatic] = useState(true);
  const viewport = useRef<HTMLDivElement>(null);
  useScrollMemory(viewport, memoryKey + ':scroll');
  useEffect(() => {
    try {
      localStorage.setItem(memoryKey + ':count', String(count));
    } catch {
      /* Memory is optional. */
    }
  }, [count, memoryKey]);
  const timer = useRef<number | undefined>(undefined),
    busy = useRef(false);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const load = () => {
    if (busy.current || count >= 12) return;
    busy.current = true;
    setLoading(true);
    setError(undefined);
    timer.current = window.setTimeout(() => {
      if (fail) {
        setError('Не удалось загрузить следующие колонки. Показанные данные сохранены.');
        setFail(false);
      } else setCount((n) => Math.min(12, n + 2));
      setLoading(false);
      busy.current = false;
    }, 1100);
  };
  const columns: WideColumn<(typeof ROWS)[number]>[] = MONTHS.slice(0, count).map((title, i) => ({
    id: String(i),
    title,
    numeric: true,
    render: (row) => <Money minorUnits={amount(Number(row.id) - 1, i)} currency="RUB" />,
    total: <Money minorUnits={ROWS.reduce((sum, _, row) => sum + amount(row, i), 0)} currency="RUB" />,
  }));
  return (
    <div data-demo="table" className="flex min-w-0 flex-col gap-4">
      <p className="text-sm text-fg-muted">
        Первая колонка, шапка и итоги остаются на месте. Прокрути вправо или добавь месяцы кнопкой. Место и
        загруженные месяцы сохраняются при возвращении.
      </p>
      <div className="flex flex-wrap items-center gap-4">
        <Switch label="Подгружать при прокрутке" checked={automatic} onCheckedChange={setAutomatic} />
        <Checkbox
          label="Сбой следующей подгрузки колонок"
          checked={fail}
          onCheckedChange={(v) => setFail(v === true)}
        />
      </div>
      <WideTable
        rows={ROWS}
        rowKey={(row) => row.id}
        rowLabel={(row) => row.title}
        columns={columns}
        loading={loading}
        error={error}
        hasMore={count < 12}
        onLoadMore={load}
        automatic={automatic}
        viewportRef={viewport}
        pendingColumns={MONTHS.slice(count, count + 2).map((title, i) => ({
          id: String(count + i),
          title,
          numeric: true,
        }))}
        label="Расходы по месяцам — вымышленные данные"
      />
    </div>
  );
}
